import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import analyticsService from '../services/analyticsService';
import adConsentService, { AdConsentState, adConsentParagraphs } from '../services/adConsentService';
import { useOptionalAuth } from '../contexts/AuthContext';
import { t, currentLang } from '../i18n';

const ACCENT = '#3fced1';

/**
 * DSGVO-Consent-Banner: erscheint, solange der Nutzer noch nicht
 * entschieden hat (localStorage 'dc-cookie-consent' leer).
 *
 * "Alle akzeptieren" → GA4 wird sofort geladen.
 * "Nur notwendige"  → es wird NICHTS geladen (strikt Opt-in, siehe
 * analyticsService). Beide Wege blenden das Banner dauerhaft aus.
 *
 * Ohne konfigurierte Measurement-ID gibt es nichts einzuwilligen —
 * dann rendert das Banner gar nicht (z. B. lokale Entwicklung).
 */
/** PURE: Banner zeigen? Statistik offen ODER (eingeloggt und Werbe-Entscheidung für die aktuelle Version offen) */
export function shouldShowConsentBanner(statsConsent: 'granted' | 'denied' | null, loggedIn: boolean, ad: AdConsentState | null): boolean {
  if (statsConsent === null) return true;
  return loggedIn && ad?.needsDecision === true;
}

const CookieConsentBanner: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [statsConsent, setStatsConsent] = useState(analyticsService.getConsent());
  // Werbe-Einwilligung (Customer Match, 2026-10-06): nur für Konto-Nutzer, Stand + Text vom Backend
  const auth = useOptionalAuth();
  const loggedIn = !!auth?.user;
  const [ad, setAd] = useState<AdConsentState | null>(null);
  const [adChecked, setAdChecked] = useState(true);
  const lang = currentLang();

  useEffect(() => {
    if (!loggedIn) { setAd(null); return; }
    let cancelled = false;
    adConsentService.getMine().then((s) => { if (!cancelled) { setAd(s); analyticsService.setAdConsent(s.personalizedAds); } }).catch(() => { if (!cancelled) setAd(null); });
    return () => { cancelled = true; };
  }, [loggedIn]);

  useEffect(() => {
    if (!analyticsService.measurementId && !loggedIn) return;
    setVisible(shouldShowConsentBanner(statsConsent, loggedIn, ad));
  }, [statsConsent, loggedIn, ad]);

  useEffect(() => analyticsService.onConsentChange((status) => setStatsConsent(status)), []);

  if (!visible) return null;

  const decide = async (status: 'granted' | 'denied') => {
    if (analyticsService.measurementId) analyticsService.setConsent(status);
    setStatsConsent(status);
    if (loggedIn && ad?.needsDecision) {
      const granted = status === 'granted' && adChecked;
      try {
        const next = await adConsentService.setMine(granted);
        setAd(next);
        analyticsService.setAdConsent(next.personalizedAds);
      } catch {
        setAd((prev) => (prev ? { ...prev, needsDecision: false } : prev)); // Fehler: nicht blockieren, beim nächsten Besuch erneut fragen
      }
    }
    setVisible(false);
  };

  return (
    <div
      role="dialog"
      aria-label={t('web.component.cookie-consent-banner.cookie-einwilligung')}
      className="fixed inset-x-0 bottom-0 z-[90] p-3 sm:p-4"
    >
      <div className="mx-auto max-w-3xl rounded-2xl border border-zinc-700 bg-zinc-900/95 p-4 shadow-2xl shadow-black/60 backdrop-blur-md sm:p-5">
        <p className="text-sm font-semibold text-white">{t('web.component.cookie-consent-banner.cookies-statistik')}</p>
        <p className="mt-1 text-xs leading-relaxed text-gray-400 sm:text-sm">
          {t('web.component.cookie-consent-banner.wir-nutzen-google-analytics-um')}{' '}
          <RouterLink
            to="/dartcloud/datenschutz"
            className="font-medium underline underline-offset-2 hover:text-white"
            style={{ color: ACCENT }}
          >
            {t('web.component.cookie-consent-banner.datenschutzerklaerung')}
          </RouterLink>{' '}
          und{' '}
          <RouterLink
            to="/dartcloud/cookie-richtlinie"
            className="font-medium underline underline-offset-2 hover:text-white"
            style={{ color: ACCENT }}
          >
            {t('web.component.cookie-consent-banner.cookie-richtlinie')}
          </RouterLink>
          .
        </p>
        {loggedIn && ad?.needsDecision && (
          <label className="mt-3 flex cursor-pointer items-start gap-3 rounded-xl border border-zinc-800 bg-zinc-950/60 p-3" data-testid="ad-consent-box">
            <input type="checkbox" checked={adChecked} onChange={(e) => setAdChecked(e.target.checked)} className="mt-0.5 h-4 w-4 shrink-0 accent-[#3fced1]" data-testid="ad-consent-checkbox" />
            <span className="min-w-0">
              <span className="block text-sm font-semibold text-white">{(lang.startsWith('en') ? ad.title?.en : ad.title?.de) || t('web.component.cookie-consent-banner.personalisierte-werbung')}</span>
              {adConsentParagraphs(ad, lang).map((p, i) => <span key={i} className="mt-1 block text-xs leading-relaxed text-gray-400">{p}</span>)}
            </span>
          </label>
        )}
        <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={() => decide('denied')}
            className="rounded-xl border border-zinc-700 px-4 py-2 text-sm font-semibold text-gray-300 transition-colors hover:border-zinc-500 hover:text-white"
          >
            {t('web.component.cookie-consent-banner.nur-notwendige')}
          </button>
          <button
            type="button"
            onClick={() => decide('granted')}
            className="rounded-xl px-4 py-2 text-sm font-semibold text-zinc-950 transition-opacity hover:opacity-90"
            style={{ backgroundColor: ACCENT }}
          >
            {t('web.component.cookie-consent-banner.alle-akzeptieren')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsentBanner;
